import AsyncSelect, {
  Option,
  type AsyncSelectBaseProps,
} from "@/components/AsyncSelect";
import appConfiguration from "@/components/cfg/AppCfg";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { Module } from "@/constants";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { FilterType } from "@/lib/filters";
import { ProjectRolesService } from "@/modules/projects/service/projectRole";
import { ReadProjectRole } from "@/types/projectRoleType";
import { ComponentProps, forwardRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

interface ProjectRoleSelectProps extends AsyncSelectBaseProps {
  projectId: string;
  className?: string;
  inDialog?: boolean;
  defaultItem?: Partial<Option>;
}

export default forwardRef<HTMLButtonElement, ProjectRoleSelectProps>(
  function ProjectRoleSelect({ value, projectId, defaultItem, ...props }, ref) {
    const { t } = useTranslation();
    const name = Module.ProjectRoles;
    const options = useCreateDataTableOptions(name);
    const queryKey = useDynamicQueryParams<ReadProjectRole>(
      [name, projectId],
      options,
    );

    const onFilter: ComponentProps<typeof AsyncSelect>["onChange"] =
      useCallback(
        (filterValue) => {
          options.setFilterValue("name", {
            value: filterValue,
            filter: FilterType.IncludesString,
          });
        },
        [options],
      );

    return (
      <span className="flex items-center gap-1">
        <AsyncSelect
          queryKey={queryKey}
          queryFn={() =>
            ProjectRolesService.getList(projectId, {
              filters: options.filters,
              limit: appConfiguration.selectComponentsPaginationLimit,
            })
          }
          placeholder={t("projectRoleSelect.placeholder")}
          loadingLabel={t("projectRoleSelect.loading")}
          noResultsLabel={t("projectRoleSelect.noResults")}
          searchLabel={t("projectRoleSelect.search")}
          enableFilter
          {...props}
          onFilter={onFilter}
          value={value ? value.toString() : undefined}
          ref={ref}
          defaultItem={defaultItem}
        />
      </span>
    );
  },
);
